/**
 * StageBadge — deal-stage chip on the analysis surface (Phase B, B-7).
 *
 * Fully presentational: the stage/pipeline names are resolved by the caller
 * (RecordDetailInline) from the record's crmStageId/crmPipelineId snapshot
 * (IDs only) against the cached GET /crm/stages reference. This component
 * never sees raw IDs — if a name can't be resolved (unknown stage, stale id,
 * reference unavailable in embedded mode / no CRM connection), the caller
 * passes `null`/`undefined` and the chip hides. We never render a raw CRM id.
 * The prompt-profile name lives in AnalysisPanel's provenance line (§10).
 */

import { useTranslation } from 'react-i18next';
import { Milestone } from 'lucide-react';

export interface StageBadgeProps {
  /** Resolved CRM stage name. Absent => the stage chip is hidden entirely. */
  stageName?: string | null;
  /** Resolved CRM pipeline name — only shown when `multiplePipelines` is true. */
  pipelineName?: string | null;
  /** Org has more than one CRM pipeline — disambiguate with the pipeline name. */
  multiplePipelines?: boolean;
  className?: string;
}

export function StageBadge({
  stageName,
  pipelineName,
  multiplePipelines = false,
  className,
}: StageBadgeProps) {
  const { t } = useTranslation('records');

  if (!stageName) return null;

  const showPipeline = multiplePipelines && !!pipelineName;

  return (
    <div
      data-testid="stage-badge"
      className={className ? `flex flex-col gap-1 ${className}` : 'flex flex-col gap-1'}
    >
      <span className="inline-flex w-fit items-center gap-1.5 rounded bg-bg-surface px-2 py-0.5 text-xs font-medium text-fg-muted">
        <Milestone className="h-3 w-3 shrink-0" aria-hidden="true" />
        {showPipeline
          ? t('records.analysisPanel.stageBadge.labelWithPipeline', {
              stage: stageName,
              pipeline: pipelineName,
            })
          : t('records.analysisPanel.stageBadge.label', { stage: stageName })}
      </span>
    </div>
  );
}
